import { FC } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { cdn } from '@olagg/ui-kit/cloudflareCdn'
import { hasPlaceholder } from '@olagg/validation-schemas/articleCta.schemas'
import { Measurement } from './useArtMeasurement'

interface IArtPreviewProps {
  url?: string | null
  measurement?: Measurement
  rendition: { width: number, height: number, minSourceWidth: number }
  blocking: string[]
  warnings: string[]
}

/**
 * La preview hace exactamente la misma llamada al CDN que va a hacer la app, con
 * el slot seleccionado, dentro de una caja de las dimensiones de rendición
 * reales. No es una miniatura decorativa: es para ver el recorte antes de
 * guardar. Si el slot es `sticky_mobile`, mide 48×48 de verdad: chico, pero es
 * el tamaño real al que la barra lo va a mostrar, y verlo chico acá es
 * justamente el aviso de que un arte con detalle fino no se va a leer.
 */
const ArtPreview: FC<IArtPreviewProps> = ({ url, measurement, rendition, blocking, warnings }) => {
  const clean = (url || '').trim()
  const isPlaceholder = hasPlaceholder(clean)
  const source = measurement?.status === 'measured' && measurement.width
    ? `fuente ${measurement.width}×${measurement.height}`
    : measurement?.status === 'measuring'
      ? 'midiendo…'
      : isPlaceholder
        ? 'la resuelve el servidor en el request'
        : 'sin medir'

  const cropped = measurement?.status === 'measured' && measurement.width && measurement.height
    ? Math.abs(Math.round(rendition.width / (measurement.width / measurement.height)) - rendition.height)
    : 0

  return (
    <Box mb='5'>
      <Box
        w={`${rendition.width}px`}
        h={`${rendition.height}px`}
        maxW='100%'
        bgColor='#20173d'
        border='1px dashed #433c56'
        borderRadius='6px'
        overflow='hidden'
        position='relative'
      >
        {clean && !isPlaceholder && measurement?.status === 'measured' ? (
          <img
            src={cdn(clean, { w: rendition.width, h: rendition.height, fit: 'cover', format: 'auto', q: 84 })}
            width={rendition.width}
            height={rendition.height}
            alt=''
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Flex w='full' h='full' alignItems='center' justifyContent='center' textAlign='center' p='2'>
            <Text fontSize='xs' color='gray.400'>
              {isPlaceholder ? `${clean}\nla imagen la resuelve el servidor` : `${rendition.width}×${rendition.height}`}
            </Text>
          </Flex>
        )}
      </Box>

      <Text fontSize='xs' color='gray.400' mt='1'>
        {source} · se sirve {rendition.width}×{rendition.height} · recorte {cropped} px
      </Text>

      {blocking.map(message => (
        <Text key={message} fontSize='xs' color='red.300' mt='1'>⛔ {message}</Text>
      ))}
      {warnings.map(message => (
        <Text key={message} fontSize='xs' color='orange.300' mt='1'>⚠️ {message}</Text>
      ))}
    </Box>
  )
}

export default ArtPreview
